// ── Sparkline ──────────────────────────────────────────────────────────────
// Kézi SVG, SZÁNDÉKOSAN nem Recharts: egy 26 px magas, tengely nélküli vonalhoz
// a ResponsiveContainer + a teljes chart-gépezet aránytalan (és a KPI-csíkban
// négy-öt példány futna belőle egyszerre).
//
// A vonal a TREND-et hordozza, nem a pontos értéket — ezért nincs tengelye és
// nincs tooltipje; a szám maga a KPI-kártyán áll fölötte. (Adatviz-elv: a
// sparkline kontextus egy számhoz, nem önálló diagram.)
import { useEffect, useRef, useId } from 'react';
import { usePrefersReducedMotion } from '../../hooks/useMotion';

export default function Sparkline({
  data = [],            // number[]
  color = 'var(--accent)',
  width = 132,
  height = 30,
  strokeWidth = 2,
  fill = true,
}) {
  const gid = useId().replace(/:/g, '');
  const pathRef = useRef(null);
  const reduced = usePrefersReducedMotion();

  const vals = data.filter((v) => Number.isFinite(Number(v))).map(Number);

  // Berajzolás: stroke-dasharray + dashoffset. Csak mountkor, egyszer.
  useEffect(() => {
    const p = pathRef.current;
    if (!p || reduced || vals.length < 2) return undefined;
    const len = p.getTotalLength();
    p.style.strokeDasharray = String(len);
    p.style.strokeDashoffset = String(len);
    // kényszerített reflow, hogy a kiinduló állapot érvényre jusson
    p.getBoundingClientRect();
    p.style.transition = 'stroke-dashoffset 700ms cubic-bezier(.3,.7,.3,1)';
    p.style.strokeDashoffset = '0';
    return undefined;
    // ⚠ Üres dependency-lista: a berajzolás EGYSZER fut, mountkor. `vals`-t
    // beletenni azt jelentené, hogy minden adatfrissítés újrarajzolja a vonalat.
  }, []);

  if (vals.length < 2) return null;

  const pad = strokeWidth;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = (max - min) || 1;
  const X = (i) => pad + (i * (width - pad * 2)) / (vals.length - 1);
  const Y = (v) => pad + (height - pad * 2) - ((v - min) / range) * (height - pad * 2);

  const d = vals.map((v, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(' ');
  const area = `${d} L${X(vals.length - 1).toFixed(1)} ${height} L${X(0).toFixed(1)} ${height} Z`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', overflow: 'visible' }} aria-hidden="true" focusable="false">
      {fill && (
        <>
          <defs>
            <linearGradient id={`sp${gid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.26" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#sp${gid})`} />
        </>
      )}
      <path ref={pathRef} d={d} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeLinecap="round" strokeLinejoin="round" />
      {/* A záró pont kiemelése: a sparkline-on ez az EGYETLEN „mostani" érték. */}
      <circle cx={X(vals.length - 1)} cy={Y(vals[vals.length - 1])} r={strokeWidth + 1}
        fill={color} stroke="var(--bg-card)" strokeWidth="1.5" />
    </svg>
  );
}
